import request from 'utils/request'
import { combineReducers } from 'redux'
import { loadingActions } from 'store/loader'
import { modalActions } from 'store/modal'
import { getCurrentUTCDateTime, getUTCOffset } from 'utils/time'
import { INSTRUMENT_EVENTS_MAP, LABEL } from 'utils/constants'
import { getCaregiverSelectedParticipantId } from 'utils/events'
import { navigateTo } from 'navigation'

import {
  checkAndAddCompletedQuestion,
  checkAndAddQuestion,
  normalizeChoiceAnswerForSubmit,
  resolveChoiceIdFromStoredAnswer,
} from '../utils/instrumentUtils'
import { pickTimedMatchingGridSubmitAnswer } from '../components/QuestionTypes/TimedMatchingGrid/timedMatchingGridUtils'
import {
  onFetchInstrumentsByArchitecture,
  sendEvent,
} from '../../Activities/modules/Instruments'
import {
  getInstrumentFromLocalStorage,
  getInstrumentSchedule,
  removeInstrumentFromLocalStorage,
  saveInstrumentToLocalStorage,
  saveCompletedEntryForReview,
  saveLocalCompletionStamp,
  removeLocalCompletionStamp,
  resolveInstanceIdForInstrument,
  markPersistentInstrumentSubmitted,
  isPersistentInstrumentGroup,
  getInstrumentInstanceGroup,
} from '../../Activities/utils/instrumentUtils'
import {
  collectImageFilesFromSurvey,
  getPhotoFilenamesFromSurvey,
  isBrowserFileUrl,
  stripClientOnlyImageFields,
} from '../utils/imageCaptureUpload'

import JSZip from 'jszip'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'

const SET_WEB_SURVEY = 'SET_WEB_SURVEY'
const RESET_WEB_SURVEY = 'RESET_WEB_SURVEY'
const ADD_QUESTION = 'ADD_QUESTION'
const RESET_CURRENT_SURVEY = 'RESET_CURRENT_SURVEY'
const SET_SUBMISSION_SURVEY = 'SET_SUBMISSION_SURVEY'
const ADD_COMPLETED_QUESTION = 'ADD_COMPLETED_QUESTION'
const DELETE_STAGED_FILE = 'DELETE_STAGED_FILE'
const RESET_SUBMISSION_SURVEY = 'RESET_SUBMISSION_SURVEY'
const SET_ALL_REQUIRED_QUESTIONS_ANSWERED =
  'SET_ALL_REQUIRED_QUESTIONS_ANSWERED'

// Web Survey:        The survey object pulled from the database

// Current Survey:    The current survey questions the participant is taking.
//                      Stored as an array of question IDs.

// Submission Survey: The survey.json that will be submitted

// web survey actions
export const setWebSurvey = (instrument) => {
  return {
    type: SET_WEB_SURVEY,
    instrument,
  }
}

export const resetWebSurvey = () => {
  return {
    type: RESET_WEB_SURVEY,
  }
}

export const onResetWebSurvey = () => {
  return (dispatch) => {
    dispatch(resetWebSurvey())
  }
}

// current survey actions (the survey the participant is current taking)
export const addQuestion = (questionIds, order, justCompletedQuestion) => {
  return {
    type: ADD_QUESTION,
    questionIds,
    order,
    justCompletedQuestion,
  }
}

export const resetCurrentSurvey = () => {
  return {
    type: RESET_CURRENT_SURVEY,
  }
}

export const onResetCurrentSurvey = () => {
  return (dispatch) => {
    dispatch(resetCurrentSurvey())
  }
}

const deleteStagedFile = (fileName) => {
  return {
    type: DELETE_STAGED_FILE,
    fileName,
  }
}

// submission survey actions (actions used to modify the survey.json that will be submitted)

const completeQuestion = (question, order) => {
  return {
    type: ADD_COMPLETED_QUESTION,
    question,
    order,
  }
}

const setAllRequiredAnswered = (
  allAnswered = false,
  id,
  order,
  siblingOrder,
) => {
  return {
    type: SET_ALL_REQUIRED_QUESTIONS_ANSWERED,
    allAnswered,
    id,
    order,
    siblingOrder,
  }
}

const setSubmissionSurvey = (survey) => {
  return {
    type: SET_SUBMISSION_SURVEY,
    survey,
  }
}

export const onSetSubmissionSurvey = (survey) => {
  return (dispatch) => {
    dispatch(setSubmissionSurvey(survey))
  }
}

const resetSubmissionSurvey = () => {
  return {
    type: RESET_SUBMISSION_SURVEY,
  }
}

export const onResetSubmissionSurvey = () => {
  return (dispatch) => {
    dispatch(resetSubmissionSurvey())
  }
}

const fetchWebSurvey = (url) => {
  return (dispatch) => {
    return dispatch(
      request({
        method: 'GET',
        url,
        success: (payload) => {
          return Promise.resolve(payload)
        },
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.instrumentDownloadFailed,
            level: 'error',
            tags: {
              flow: 'instrument',
              action: 'download_failed',
            },
          })
        },
      }),
    )
  }
}

const imageRegex = /<img\b[^>]*>/gi
const imgSrcRegex = /<img\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi
const pOpen = /<p>/g
const pClose = /<\/p>/g
const ZIP_MEDIA_EXT =
  /\.(png|jpe?g|gif|webp|svg|bmp|mp4|webm|mov|m4v|mp3|wav|m4a|aac|ogg)$/i

// GOLD-12178: TMG symbols are SVG or PNG. JSZip blobs often have an
// empty type; Chrome will not paint SVG in <img> without image/svg+xml.
const TMG_SYMBOL_MIME = {
  svg: 'image/svg+xml',
  png: 'image/png',
}

const createZipMediaObjectUrl = (blob, path) => {
  const ext = String(path).split('.').pop()?.toLowerCase()
  const mime = TMG_SYMBOL_MIME[ext]
  const typedBlob =
    mime && blob?.type !== mime ? new Blob([blob], { type: mime }) : blob
  return URL.createObjectURL(typedBlob)
}

const removeImageElementFromLabel = (label) => {
  let newLabel = label.slice()
  newLabel = newLabel.replace(imageRegex, '')
  newLabel = newLabel.replace(pOpen, '')
  newLabel = newLabel.replace(pClose, '')
  return newLabel
}


const getFileBasename = (path = '') =>
  String(path).split(/[\\/]/).pop() || String(path)

const extractImgSrcsFromHtml = (html) => {
  if (typeof html !== 'string' || !html) return []
  const srcs = []
  imgSrcRegex.lastIndex = 0
  let match
  while ((match = imgSrcRegex.exec(html))) {
    if (match[1]) srcs.push(match[1])
  }
  return srcs
}

const isAbsoluteMediaUrl = (value) => {
  if (typeof value !== 'string' || !value) return false
  return /^(blob:|data:|https?:|\/\/)/i.test(value)
}

// GOLD-12178: Resolve symbol images from survey.zip so the key row can
// render blob/data URLs instead of zip-relative paths that the <img>
// tag cannot load.
const resolveTimedMatchingGridSymbolKey = (symbolKey, mediaMap) => {
  if (!Array.isArray(symbolKey)) return symbolKey
  return symbolKey.map((entry) => {
    if (!entry || typeof entry !== 'object') return entry
    const ref =
      entry.imageSrc ||
      entry.image_url ||
      entry.imageUrl ||
      entry.image ||
      entry.src
    const resolved = resolveZipMediaRef(ref, mediaMap)
    if (!resolved) return entry
    return {
      ...entry,
      image: resolved,
      imageSrc: resolved,
      image_url: resolved,
    }
  })
}

const resolveZipMediaRef = (ref, mediaMap = {}) => {
  if (!ref || typeof ref !== 'string') return null
  if (isAbsoluteMediaUrl(ref)) return ref
  const basename = getFileBasename(ref)
  return (
    mediaMap[ref] ||
    mediaMap[basename] ||
    mediaMap[basename.toLowerCase()] ||
    mediaMap[ref.toLowerCase()] ||
    null
  )
}

const resolveImageSrcValue = (value, mediaMap) => {
  if (!value) return undefined
  if (Array.isArray(value)) {
    const resolved = value
      .map((item) => resolveZipMediaRef(item, mediaMap))
      .filter(Boolean)
    if (!resolved.length) return undefined
    return resolved.length === 1 ? resolved[0] : resolved
  }
  return resolveZipMediaRef(value, mediaMap) || undefined
}

const buildZipMediaUrlMap = async (zip) => {
  const mediaMap = {}
  const filePaths = Object.keys(zip?.files || {})

  await Promise.all(
    filePaths.map(async (path) => {
      const file = zip.files[path]
      if (!file || file.dir || !ZIP_MEDIA_EXT.test(path)) return
      const blob = await file.async('blob')
      const objectUrl = createZipMediaObjectUrl(blob, path)
      const basename = getFileBasename(path)
      mediaMap[path] = objectUrl
      mediaMap[path.toLowerCase()] = objectUrl
      mediaMap[basename] = objectUrl
      mediaMap[basename.toLowerCase()] = objectUrl
    }),
  )

  return mediaMap
}

/**
 * Attach zip media to survey JSON
 * @param {Object} surveyJson
 * @param {Object} mediaMap
 * @returns {Object} surveyJson
 * @description This function attaches zip media to the survey JSON.
 * @description It resolves the media src value for the question label, image, and media.
 * @description It also resolves the media src value for the choice label and image.
 * @description It returns the survey JSON with the media src values attached.
 */
const attachZipMediaToSurvey = (surveyJson, mediaMap = {}) => {
  const questions = surveyJson?.questions || {}
  Object.keys(questions).forEach((questionId) => {
    const question = questions[questionId]
    if (!question || typeof question !== 'object') return

    const labelImgSrcs = extractImgSrcsFromHtml(question.label)
      .map((src) => resolveZipMediaRef(src, mediaMap))
      .filter(Boolean)

    const resolvedQuestionImage =
      resolveImageSrcValue(question.imageSrc, mediaMap) ||
      resolveZipMediaRef(question.image, mediaMap) ||
      (labelImgSrcs.length === 1
        ? labelImgSrcs[0]
        : labelImgSrcs.length > 1
          ? labelImgSrcs
          : undefined)

    if (resolvedQuestionImage) {
      question.imageSrc = resolvedQuestionImage
    }

    const resolvedMedia =
      resolveZipMediaRef(question.mediaSrc, mediaMap) ||
      resolveZipMediaRef(question.media, mediaMap)
    if (resolvedMedia) {
      question.mediaSrc = resolvedMedia
    }

    Object.keys(question.choices || {}).forEach((choiceId) => {
      const choice = question.choices[choiceId]
      if (!choice || typeof choice !== 'object') return

      const choiceLabelImgSrcs = extractImgSrcsFromHtml(choice.label)
        .map((src) => resolveZipMediaRef(src, mediaMap))
        .filter(Boolean)

      const resolvedChoiceImage =
        resolveImageSrcValue(choice.imageSrc, mediaMap) ||
        resolveZipMediaRef(choice.image, mediaMap) ||
        (choiceLabelImgSrcs.length === 1
          ? choiceLabelImgSrcs[0]
          : choiceLabelImgSrcs.length > 1
            ? choiceLabelImgSrcs
            : undefined)

      if (resolvedChoiceImage) {
        choice.imageSrc = resolvedChoiceImage
      }
    })

    // GOLD-12178: TMG key images live in the zip, same as choice images.
    const details =
      question.attributes?.timed_matching_grid_details ||
      question.attributes?.timedMatchingGridDetails
    if (details?.symbol_key) {
      details.symbol_key = resolveTimedMatchingGridSymbolKey(
        details.symbol_key,
        mediaMap,
      )
    }
  })

  return surveyJson
}

const prepareSurvey = (surveyJson) => {
  const instrumentType = (
    surveyJson?.type ||
    surveyJson?.instrument_type ||
    surveyJson?.metadata?.instrument_type ||
    ''
  )
    .toString()
    .toUpperCase()
  const shouldPrepare =
    ['SURVEY', 'EDIARY'].includes(instrumentType) || !!surveyJson?.questions

  if (shouldPrepare && surveyJson?.questions) {
    const { questions } = surveyJson
    const questionArr = Object.keys(questions)
    questionArr.forEach((questionId) => {
      const question = questions[questionId]
      if (!question || typeof question !== 'object') return
      if (typeof question.label === 'string') {
        question.label = removeImageElementFromLabel(question.label)
      }
      const choiceIds = Object.keys(question.choices || {})
      choiceIds.forEach((choiceId) => {
        const choice = question.choices[choiceId]
        if (
          (choice?.image || choice?.imageSrc) &&
          typeof choice.label === 'string'
        ) {
          choice.label = removeImageElementFromLabel(choice.label)
        }
      })
    })
  }
  return surveyJson
}

const calculateQuestionScoreTotal = (
  submissionQuestions = [],
  questionMap = {},
) => {
  let total = 0

  submissionQuestions.forEach((question) => {
    const questionId = question?.id
    if (!questionId) return

    const sourceQuestion = questionMap?.[questionId]
    const choices = sourceQuestion?.choices
    if (!choices) return

    const selectedChoiceId = resolveChoiceIdFromStoredAnswer(
      choices,
      question?.answer?.[0],
    )
    if (!selectedChoiceId) return

    const scoreValue = choices?.[selectedChoiceId]?.score_value
    if (scoreValue === undefined || scoreValue === null || scoreValue === '')
      return

    const numericScore = Number(scoreValue)
    if (!Number.isNaN(numericScore)) {
      total += numericScore
    }
  })

  return total
}

const SCORE_OPERATIONS = {
  SUM: (values) => values.reduce((acc, value) => acc + value, 0),
  MEAN: (values) =>
    values.length
      ? values.reduce((acc, value) => acc + value, 0) / values.length
      : 0,
  PRODUCT: (values) => values.reduce((acc, value) => acc * value, 1),
}

const sanitizeNumericValue = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const safeEvaluateExpression = (expression) => {
  if (!expression || typeof expression !== 'string') return 0
  const normalized = expression.replace(/\^/g, '**').replace(/\s/g, '')
  if (!normalized) return 0
  // Only allow numeric expressions and arithmetic operators.
  if (!/^[\d.+\-*/()]+$/.test(normalized)) return 0
  try {
    const evaluated = Function(`"use strict"; return (${normalized});`)() // eslint-disable-line no-new-func
    return Number.isFinite(evaluated) ? evaluated : 0
  } catch (e) {
    return 0
  }
}

const getQuestionAnswerScoreValues = (
  submittedQuestion = {},
  sourceQuestion = {},
) => {
  const answers = Array.isArray(submittedQuestion?.answer)
    ? submittedQuestion.answer
    : []
  const choices = sourceQuestion?.choices || {}
  const values = []

  answers.forEach((answer) => {
    const selectedChoiceId = resolveChoiceIdFromStoredAnswer(choices, answer)
    const choiceScore = selectedChoiceId
      ? choices?.[selectedChoiceId]?.score_value
      : undefined
    const answerScore =
      choiceScore !== undefined && choiceScore !== null && choiceScore !== ''
        ? choiceScore
        : answer?.score_value
    const numeric = sanitizeNumericValue(answerScore)
    if (numeric !== null) {
      values.push(numeric)
    }
  })

  return values.length ? values : [0]
}

const getScoreValues = (
  order = [],
  questionMap = {},
  submissionQuestions = [],
) => {
  const output = {}
  const submissionById = {}
  submissionQuestions.forEach((question) => {
    if (question?.id) {
      submissionById[question.id] = question
    }
  })

  order.forEach((questionId, idx) => {
    const sourceQuestion = questionMap?.[questionId]
    if (!sourceQuestion) return
    const submittedQuestion = submissionById?.[questionId] || {}
    const values = getQuestionAnswerScoreValues(
      submittedQuestion,
      sourceQuestion,
    )
    const questionFormula = sourceQuestion?.formula

    output[questionId] = {
      name: `Q${idx + 1}`,
      values,
      formula: questionFormula,
    }
  })

  return output
}

const getDomainQuestionIds = (questionsWithScoring = {}, domainId) => {
  if (!questionsWithScoring) return []

  if (Array.isArray(questionsWithScoring)) {
    const domainMatch = questionsWithScoring.find(
      (entry) =>
        entry?.domain_id === domainId ||
        entry?.domainId === domainId ||
        entry?.id === domainId,
    )
    if (domainMatch) {
      return (
        domainMatch?.questions ||
        domainMatch?.question_ids ||
        domainMatch?.questionIds ||
        []
      )
    }
    return []
  }

  const direct = questionsWithScoring?.[domainId]
  if (Array.isArray(direct)) return direct

  // Alternate structure: { questionId: domainId }
  return Object.keys(questionsWithScoring).filter(
    (questionId) => questionsWithScoring?.[questionId] === domainId,
  )
}

const aggregateValues = (operation = 'SUM', values = []) => {
  const handler = SCORE_OPERATIONS?.[operation] || SCORE_OPERATIONS.SUM
  return handler(values)
}

const calculateSingleDomainValue = (
  operation,
  scoreValues,
  questionIds = [],
) => {
  const perQuestionValues = questionIds.map((questionId) => {
    const questionScore = scoreValues?.[questionId]
    if (!questionScore) {
      return operation === 'PRODUCT' ? 1 : 0
    }
    const values = questionScore?.values || [0]
    const questionOperation =
      questionScore?.formula || (values.length > 1 ? 'MEAN' : 'SUM')
    return aggregateValues(questionOperation, values)
  })

  return aggregateValues(operation, perQuestionValues)
}

const calculateDomainValues = (
  questionsWithScoring = {},
  scoreValues = {},
  domains = {},
) => {
  const output = {}
  if (!domains || typeof domains !== 'object') return output

  Object.keys(domains).forEach((domainId) => {
    const operation = domains?.[domainId]?.formula || 'SUM'
    const questions = getDomainQuestionIds(questionsWithScoring, domainId)
    output[domainId] = {
      operation,
      questions,
      value: calculateSingleDomainValue(operation, scoreValues, questions),
    }
  })

  return output
}

const calculateScoreValue = (domainValues = {}, formula = '') => {
  if (!formula) return 0
  let replacedFormula = formula
  Object.keys(domainValues).forEach((domainId) => {
    const escapedId = domainId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Domain ids can include non-word chars (for example "#a"), so word
    // boundaries are not reliable for replacement.
    const idRegex = new RegExp(
      `(^|[^A-Za-z0-9_])(${escapedId})(?=$|[^A-Za-z0-9_])`,
      'g',
    )
    replacedFormula = replacedFormula.replace(
      idRegex,
      `$1${domainValues?.[domainId]?.value}`,
    )
  })

  const evaluated = safeEvaluateExpression(replacedFormula)
  const asString = `${evaluated}`
  if (asString.includes('.') && asString.split('.')[1].length > 2) {
    return Number(evaluated.toFixed(2))
  }
  return evaluated
}

const createFormulaScoring = (survey, questionMap = {}) => {
  const { questionsWithScoring, order, scores, domains } = survey || {}
  if (!Array.isArray(scores) || !scores.length || !domains) return []

  const scoreValues = getScoreValues(
    order?.[0] || [],
    questionMap,
    survey?.questions || [],
  )
  const domainValues = calculateDomainValues(
    questionsWithScoring,
    scoreValues,
    domains,
  )

  return scores.map((score) => ({
    id: score?.id,
    name: score?.name,
    description: score?.description,
    value: calculateScoreValue(domainValues, score?.formula || ''),
  }))
}

const prepareSurveyForSubmission = (survey, questionMap = {}) => {
  const output = { ...survey }
  const scoreTotal = calculateQuestionScoreTotal(
    output?.questions || [],
    questionMap,
  )
  const computedFormulaScoring = createFormulaScoring(output, questionMap)
  const resolvedScoring = computedFormulaScoring.length
    ? computedFormulaScoring
    : output?.scoring || []

  if (Array.isArray(output?.scores) && output.scores.length > 0) {
    output.scores = output.scores.map((score) => {
      const scoringValue = resolvedScoring?.find(
        (scoringObj) => score?.id === scoringObj?.id,
      )?.value
      const resolvedValue =
        scoringValue !== undefined &&
        scoringValue !== null &&
        scoringValue !== ''
          ? Number(scoringValue)
          : Number(scoreTotal)

      return {
        ...score,
        final_value: Number.isNaN(resolvedValue) ? 0 : resolvedValue,
      }
    })
  }

  delete output.scoring
  return output
}

let fetchGeneration = 0

const isWebSurveyPath = (pathname) => (pathname || '').includes('/web-survey')

export const onFetchWebSurvey = (url) => {
  return (dispatch, getState) => {
    const generation = ++fetchGeneration
    const isStale = () => {
      if (generation !== fetchGeneration) return true
      const pathname = getState()?.location?.pathname || ''
      return !isWebSurveyPath(pathname)
    }

    dispatch(loadingActions.startLoader(true))
    return dispatch(fetchWebSurvey(url))
      .then((payload) => {
        if (isStale()) return
        if (payload.type !== 'application/zip') {
          const newPayload = { ...payload }
          const { user, instruments, webSurveys } = getState()
          const { instancesList, allInstancesList } = instruments
          const preferredInstanceId = webSurveys?.submissionSurvey?.instance_id
          if (instancesList?.length > 0 || allInstancesList?.length > 0) {
            const participantId = user?.is_caregiver
              ? getCaregiverSelectedParticipantId() || user?.id
              : user?.id
            const storedInstrument = getInstrumentFromLocalStorage({
              instrumentId: payload.id,
              participantId,
              instanceId: preferredInstanceId,
            })
            const resolvedInstanceId = resolveInstanceIdForInstrument({
              instrumentId: payload.id,
              instancesList,
              allInstancesList,
              participantId,
              existingInstanceId: preferredInstanceId,
              inProgressDraft: storedInstrument,
            })
            newPayload.instance_id = resolvedInstanceId
            // send event
            dispatch(
              sendEvent(
                payload.id,
                INSTRUMENT_EVENTS_MAP.read,
                newPayload.instance_id,
              ),
            )
          }
          const { order, questions, id, metadata = {} } = newPayload

          const participantId = user?.is_caregiver
            ? getCaregiverSelectedParticipantId() || user?.id
            : user?.id

          const storedInstrument = getInstrumentFromLocalStorage({
            instrumentId: id || newPayload.identity || newPayload.instrument_id,
            participantId,
            instanceId: preferredInstanceId || newPayload.instance_id,
          })

          const hasScoringConfig =
            Array.isArray(newPayload?.scores) &&
            newPayload.scores.length > 0 &&
            newPayload?.domains &&
            newPayload?.questionsWithScoring

          const hasHorizontalSelectOne = Object.values(questions || {}).some(
            (q) => q?.type === 'horizontal_select_one',
          )

          const reviewEnabled = metadata?.review_screen?.enabled === true
          const scoresArray = Array.isArray(newPayload?.scores)
            ? newPayload.scores
            : []
          // Prefer per-score display flags. If none are explicitly enabled, fall back
          // to legacy/instrument-level `metadata.display_score_to_participant`.
          const hasAnyScoreToDisplay = scoresArray.some(
            (score) => score?.display_score_to_participant === true,
          )
          const shouldDisplayScores =
            hasAnyScoreToDisplay ||
            (metadata?.display_score_to_participant === true &&
              scoresArray.length > 0)

          // Base formatted payload (inject resume-review for saved instruments)
          let formattedPayload = !!storedInstrument
            ? {
                ...newPayload,
                order: [['resume-review', ...order?.[0]]],
                questions: {
                  'resume-review': {
                    type: 'resume-review',
                    label: LABEL,
                    choices: {},
                    choices_order: [],
                    attributes: {
                      required: true,
                      hasCharLimit: false,
                    },
                    logic: {},
                    labelJson: {
                      blocks: [
                        {
                          key: 'key',
                          text: 'text',
                          type: 'unstyled',
                          depth: 0,
                          inlineStyleRanges: [],
                          entityRanges: [],
                          data: {},
                        },
                      ],
                      entityMap: {},
                    },
                  },
                  ...questions,
                },
              }
            : newPayload

          // When scoring is configured, there is at least one horizontal_select_one,
          // and scoring is configured to be displayed, append a synthetic
          // score-summary page after the last real question.
          if (hasScoringConfig && shouldDisplayScores) {
            const baseOrder = formattedPayload?.order?.[0] || []
            const scoreSummaryId = 'score-summary'

            // Only add once.
            if (!baseOrder.includes(scoreSummaryId)) {
              const nextOrder0 = [...baseOrder, scoreSummaryId]
              const scoreName =
                scoresArray.find(
                  (s) => s?.display_score_to_participant === true,
                )?.name ||
                scoresArray?.[0]?.name ||
                'Score'

              formattedPayload = {
                ...formattedPayload,
                order: [nextOrder0],
                questions: {
                  ...formattedPayload.questions,
                  [scoreSummaryId]: {
                    type: 'score-summary',
                    label: scoreName,
                    choices: {},
                    choices_order: [],
                    attributes: {
                      required: false,
                    },
                    logic: {},
                  },
                },
              }
            }
          }

          dispatch(setWebSurvey(formattedPayload))
          dispatch(loadingActions.stopLoader(true))
          if (!(instancesList?.length > 0 || allInstancesList?.length > 0)) {
            logSentryEvent({
              message: SENTRY_MESSAGES.instrumentSeen,
              tags: {
                flow: 'instrument',
                action: 'seen',
                instrument_id:
                  newPayload?.id != null ? String(newPayload.id) : undefined,
                instance_id:
                  newPayload?.instance_id != null
                    ? String(newPayload.instance_id)
                    : undefined,
              },
            })
          }
          return Promise.reject('No JSZip needed') // Break the promise chain
        } else {
          return Promise.resolve(payload)
        }
      })
      .then(JSZip.loadAsync)
      .then(async (zip) => {
        if (isStale()) return
        const fileNameArr = Object.keys(zip?.files || {})
        const jsonFileName = fileNameArr.find(
          (val) => val.toLowerCase().includes('.json') && !zip.files[val]?.dir,
        )
        if (!jsonFileName) {
          logSentryEvent({
            message: SENTRY_MESSAGES.instrumentParsingFailed,
            level: 'error',
            tags: {
              flow: 'instrument',
              action: 'parsing_failed',
            },
          })
          throw new Error('Zip survey is missing a JSON instrument file')
        }

        const data = await zip.file(jsonFileName).async('string')
        const _data = JSON.parse(data)

        const { user, instruments, webSurveys } = getState()
        const { instancesList, allInstancesList } = instruments
        const preferredInstanceId = webSurveys?.submissionSurvey?.instance_id
        if (instancesList?.length > 0 || allInstancesList?.length > 0) {
          const participantId = user?.is_caregiver
            ? getCaregiverSelectedParticipantId() || user?.id
            : user?.id
          const storedInstrument = getInstrumentFromLocalStorage({
            instrumentId: _data.id,
            participantId,
            instanceId: preferredInstanceId,
          })
          _data.instance_id = resolveInstanceIdForInstrument({
            instrumentId: _data.id,
            instancesList,
            allInstancesList,
            participantId,
            existingInstanceId: preferredInstanceId,
            inProgressDraft: storedInstrument,
          })
        }

        const mediaMap = await buildZipMediaUrlMap(zip)
        attachZipMediaToSurvey(_data, mediaMap)
        if (isStale()) return
        dispatch(setWebSurvey(_data))
        dispatch(loadingActions.stopLoader(true))
        logSentryEvent({
          message: SENTRY_MESSAGES.instrumentSeen,
          tags: {
            flow: 'instrument',
            action: 'seen',
            instrument_id: _data?.id != null ? String(_data.id) : undefined,
            instance_id:
              _data?.instance_id != null ? String(_data.instance_id) : undefined,
          },
        })
      })
      .catch((error) => {
        if (error !== 'No JSZip needed' && !isStale()) {
          const isParseError =
            error instanceof SyntaxError ||
            (typeof error?.message === 'string' &&
              error.message.toLowerCase().includes('json'))
          logSentryEvent({
            message: isParseError
              ? SENTRY_MESSAGES.instrumentParsingFailed
              : SENTRY_MESSAGES.instrumentLoadFailed,
            level: 'error',
            tags: {
              flow: 'instrument',
              action: isParseError ? 'parsing_failed' : 'load_failed',
            },
            error: error instanceof Error ? error : undefined,
          })
          dispatch(loadingActions.stopLoader(true))
        }
      })
  }
}

export const uploadV2File = (formData) => {
  return (dispatch) => {
    const success = () => {
      logSentryEvent({
        message: 'File upload: upload_success',
        tags: {
          flow: 'file-upload',
          'file.action': 'upload_success',
        },
        contexts: {
          fileUpload: {
            moduleName: 'web-survey',
            endpoint: '/control/v2/file/upload',
          },
        },
      })
      return true
    }
    return dispatch(
      request({
        method: 'POST',
        url: '/control/v2/file/upload',
        contentType: 'multipart/form-data',
        body: formData,
        success,
      }),
    ).catch((error) => {
      logSentryEvent({
        message: 'File upload: upload_failed',
        level: 'error',
        tags: {
          flow: 'file-upload',
          'file.action': 'upload_failed',
        },
        contexts: {
          fileUpload: {
            moduleName: 'web-survey',
            endpoint: '/control/v2/file/upload',
          },
        },
        error,
      })
      throw error
    })
  }
}

const uploadStagedBlobFiles = (stagedFiles) => (dispatch) => {
  const uploads = Object.entries(stagedFiles || {})
    .filter(([fileName, fileUrl]) => fileName && isBrowserFileUrl(fileUrl))
    .map(async ([fileName, fileUrl]) => {
      const blob = await fetch(fileUrl).then((res) => res.blob())
      const file = new File([blob], fileName, {
        type: blob.type || 'image/jpeg',
        lastModified: Date.now(),
      })
      const fData = new FormData()
      fData.set('upload_file', file)
      fData.set('file_name', fileName)
      fData.set('module_name', 'survey')
      return dispatch(uploadV2File(fData))
    })
  if (!uploads.length) return Promise.resolve()
  return Promise.all(uploads)
}

const sendTransaction = (value, timestamp, uniqueId) => {
  return (dispatch) => {
    const body = {
      timestamp,
      type: 'instrument_complete',
      unique_id: uniqueId,
      value,
    }
    return dispatch(
      request({
        method: 'POST',
        url: '/control/participants/me/transactions',
        body: JSON.stringify(body),
        success: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.creditTransaction,
            tags: {
              flow: 'payments',
              action: 'credit_transaction',
              value: Number(value) || 0,
            },
          })
        },
      }),
    )
  }
}

const addRequiredArticleSubmissionKeys = (submissionJson, articleJson) => {
  const resultSubmissionJson = submissionJson
  resultSubmissionJson.url = articleJson.url
  resultSubmissionJson.rating = 1
  resultSubmissionJson.comments = ''
}


const onSurveySubmit = (initialSurvey, isArticle = false, articleJson = {}) => {
  return (dispatch, getState) => {
    const { user, webSurveys } = getState()
    const isInstrumentArch1 =
      !user?.study?.config?.instrument_architecture_version ||
      user?.study?.config?.instrument_architecture_version < 2
    const humiIsabellaDisabled = false
    const survey = {
      ...initialSurvey,
      questions: initialSurvey?.questions?.filter((value) => {
        if (!value || Object.keys(value).length === 0) return false
        // UI-only helper questions must never be sent to backend.
        if (
          value.type === 'resume-review' ||
          value.type === 'review' ||
          value.type === 'submission'
        ) {
          return false
        }
        return true
      }),
    }
    delete survey.__reviewCompletedEntry

    const { id: study_version_id } = user?.study?.version || {}

    const {
      identity: instrumentId,
      instrument_id,
      depQId,
      version,
      gamification_points: value,
      type,
      instrument_type,
      metadata,
    } = survey
    const webSurveyState = webSurveys?.webSurvey || {}
    const resolvedInstrumentId =
      instrumentId ||
      instrument_id ||
      survey?.id ||
      webSurveyState?.identity ||
      webSurveyState?.instrument_id ||
      webSurveyState?.id
    const resolvedInstrumentType = (
      type ||
      instrument_type ||
      metadata?.instrument_type ||
      metadata?.type ||
      webSurveyState?.type ||
      webSurveyState?.instrument_type ||
      webSurveyState?.metadata?.instrument_type ||
      webSurveyState?.metadata?.type
    )?.toLowerCase()
    const resolvedVersion =
      version ||
      metadata?.version ||
      webSurveyState?.version ||
      webSurveyState?.metadata?.version
    const resolvedPoints =
      value ||
      metadata?.gamification_points ||
      webSurveyState?.gamification_points ||
      0

    const timestamp = getCurrentUTCDateTime()
    const submissionStart = survey?.start || webSurveyState?.start || timestamp

    const { instruments } = getState()
    const { instancesList, allInstancesList } = instruments

    const participantId = user?.is_caregiver
      ? getCaregiverSelectedParticipantId() || user?.id
      : user?.id
    const draftProgress = getInstrumentFromLocalStorage({
      instrumentId: resolvedInstrumentId,
      participantId,
    })

    const instance_id = resolveInstanceIdForInstrument({
      instrumentId: resolvedInstrumentId,
      instancesList,
      allInstancesList,
      participantId,
      existingInstanceId: survey?.instance_id,
      inProgressDraft: draftProgress,
    })

    const baseSurveyJSON = Object.assign({}, webSurveyState, survey, {
      start: submissionStart,
      completion: timestamp,
      state: 'COMPLETE',
      instrument_id: resolvedInstrumentId,
      identity: resolvedInstrumentId,
      instance_id,
      version_number: resolvedVersion,
      version: resolvedVersion,
      type: type || webSurveyState?.type,
      instrument_type: instrument_type || webSurveyState?.instrument_type,
      browser_info: window?.navigator?.userAgent,
      study_version_id,
    })
    const finalSurveyJSON = isInstrumentArch1
      ? baseSurveyJSON
      : prepareSurveyForSubmission(
        baseSurveyJSON,
        webSurveyState?.questions || {},
      )

    const resumedFromDraft = !!draftProgress

    // If caregiver is completing on behalf of a participant, include context
    if (user?.is_caregiver) {
      const selectedId = getCaregiverSelectedParticipantId()
      if (selectedId) {
        finalSurveyJSON.is_caregiver = true
        finalSurveyJSON.participant_id = selectedId
      }
    }

    if (isArticle) {
      addRequiredArticleSubmissionKeys(finalSurveyJSON, articleJson)
    }

    const articleParticipantKey =
      finalSurveyJSON.participant_id || user?.id
    if (
      isArticle &&
      finalSurveyJSON.instance_id &&
      articleParticipantKey &&
      resolvedInstrumentId
    ) {
      // GOLD-12476: Article opens PDF in a new tab and returns to activities
      // before POST completes; stamp completion locally so calendar pending
      // updates without a full page refresh.
      saveLocalCompletionStamp({
        participantId: articleParticipantKey,
        instrumentId: resolvedInstrumentId,
        instanceId: finalSurveyJSON.instance_id,
        lastUpdated: timestamp,
      })
    }

    const normalizeSurveyOrEDiarySubmission = (submissionJson) => {
      const webSurveyState = getState().webSurveys?.webSurvey || {}
      const stagedFiles = getState().webSurveys?.stagedFiles || {}

      const instrument_id =
        submissionJson?.instrument_id || webSurveyState?.instrument_id
      const instrument_version = submissionJson?.version_number

      const start = submissionJson?.start
      const completion = submissionJson?.completion
      const entry_date = (start || '').toString().slice(0, 10)

      const attachments = Object.keys(stagedFiles || {}).filter(Boolean)

      const questionsFromAuthoring = webSurveyState?.questions || {}
      const questions = (submissionJson?.questions || []).map((q) => {
        const authored = questionsFromAuthoring?.[q?.id] || {}
        const isTimedMatchingGrid =
          (q?.type || authored?.type) === 'timed_matching_grid'
        // GOLD-12199 / GOLD-12260: Timed sittings are never re-editable
        // from Review. Force in_review false on submit so the server
        // does not treat TMG as review content.
        const inReview = isTimedMatchingGrid
          ? false
          : authored?.attributes?.in_review ??
            q?.attributes?.in_review ??
            q?.in_review ??
            true
        const requiredBool =
          authored?.attributes?.required ??
          q?.required ??
          q?.attributes?.required
        const required =
          requiredBool === undefined ? undefined : String(!!requiredBool)

        const answer = Array.isArray(q?.answer)
          ? q.answer.map((a) => {
              if (!a || typeof a !== 'object') return a
              if (isTimedMatchingGrid) {
                // GOLD-12199 / GOLD-12181: Strip UI-only fields so the
                // POST is only the scoring contract (sequences, elapsed,
                // timed_out).
                const tmgAnswer = pickTimedMatchingGridSubmitAnswer(a)
                return {
                  ...tmgAnswer,
                  timed_out: !!tmgAnswer.timed_out,
                }
              }
              const { question, label, choice_id, id: answerId, ...rest } = a
              const choiceId =
                choice_id ||
                answerId ||
                resolveChoiceIdFromStoredAnswer(authored?.choices, a)
              const normalized = normalizeChoiceAnswerForSubmit(authored, {
                ...rest,
                ...(choiceId ? { choice_id: choiceId } : {}),
              })
              const { choice_id: normalizedChoiceId, ...answerRest } =
                normalized
              const resolvedId = normalizedChoiceId || choiceId
              return {
                ...(resolvedId && { id: resolvedId }),
                ...answerRest,
              }
            })
          : []

        const time_answered =
          q?.time_answered ||
          q?.timeAnswered ||
          completion ||
          getCurrentUTCDateTime()

        // Remove authoring/UI-only fields we don't want to submit.
        const { name, imageFiles, ...restQ } = q || {}

        return {
          ...restQ,
          id: q?.id,
          label: q?.label,
          type: q?.type,
          in_review: inReview,
          required,
          state: q?.state,
          answer,
          time_answered,
          ...(isTimedMatchingGrid
            ? {
                attributes: {
                  ...(authored?.attributes || {}),
                  ...(q?.attributes || {}),
                  in_review: false
                }
              }
            : {}),
        }
      })

      const {
        gamification_points,
        title,
        identity,
        version,
        version_number,
        version_number_name,
        browser_info,
        study_version_id,
        questions: _questions,
        ...restTop
      } = submissionJson || {}

      // `gamification_points` can still be present under metadata;
      const metadata = restTop?.metadata
        ? (() => {
            const next = { ...restTop.metadata }
            delete next.gamification_points
            return next
          })()
        : restTop?.metadata

      return {
        ...restTop,
        metadata,
        identity,
        instrument_id,
        version,
        instrument_version:
          instrument_version == null ? undefined : instrument_version,
        start,
        completion,
        entry_date,
        instance_id: submissionJson?.instance_id,
        attachments,
        questions,
      }
    }

    const shouldNormalize =
      !isInstrumentArch1 &&
      !isArticle &&
      ['survey', 'ediary'].includes(resolvedInstrumentType || '')
    const submissionPayload = shouldNormalize
      ? normalizeSurveyOrEDiarySubmission(finalSurveyJSON)
      : stripClientOnlyImageFields(finalSurveyJSON)

    if (isArticle) {
      const instrumentVersion =
        submissionPayload?.version_number ||
        submissionPayload?.version ||
        submissionPayload?.metadata?.version

      submissionPayload.instrument_version = instrumentVersion
    }

    const success = () => {
      logSentryEvent({
        message: SENTRY_MESSAGES.instrumentSubmitted,
        tags: {
          flow: 'instrument',
          action: resumedFromDraft ? 'resumed_and_submitted' : 'submitted',
          instrument_id:
            resolvedInstrumentId != null
              ? String(resolvedInstrumentId)
              : undefined,
          instance_id:
            finalSurveyJSON?.instance_id != null
              ? String(finalSurveyJSON.instance_id)
              : undefined,
        },
      })
      const uniqueId = `${instrumentId || instrument_id}:${timestamp}`
      dispatch(
        sendTransaction(
          value || metadata?.gamification_points || 0,
          timestamp,
          uniqueId,
        ),
      )

      if (!isArticle && finalSurveyJSON?.instance_id) {
        const sub = getState().webSurveys?.submissionSurvey
        const persistable = (sub?.questions || []).filter(
          (q) =>
            q &&
            ![
              'submission',
              'review',
              'resume-review',
              'resume-review-timestamp',
            ].includes(q.type),
        )
        const participantKey = finalSurveyJSON.participant_id || user?.id
        if (persistable.length && participantKey) {
          saveCompletedEntryForReview({
            participantId: participantKey,
            instrumentId: resolvedInstrumentId,
            instanceId: finalSurveyJSON.instance_id,
            questions: persistable,
            lastUpdated: timestamp,
          })
        }
      }

      const persistentGroup = getInstrumentInstanceGroup(
        [getState().instruments?.allInstancesList, instancesList],
        resolvedInstrumentId,
      )
      if (isPersistentInstrumentGroup(persistentGroup)) {
        markPersistentInstrumentSubmitted({
          participantId: finalSurveyJSON.participant_id || user?.id,
          instrumentId: resolvedInstrumentId,
        })
      }

      removeInstrumentFromLocalStorage({
        instrumentId: resolvedInstrumentId,
        participantId: user?.id,
        instanceId: finalSurveyJSON?.instance_id,
      })
      if (isArticle) {
        dispatch(onFetchInstrumentsByArchitecture(true))
      }
      navigateTo('/my-activities')
    }
    const fail = () => {
      if (
        isArticle &&
        finalSurveyJSON?.instance_id &&
        articleParticipantKey &&
        resolvedInstrumentId
      ) {
        removeLocalCompletionStamp({
          participantId: articleParticipantKey,
          instrumentId: resolvedInstrumentId,
          instanceId: finalSurveyJSON.instance_id,
        })
      }
      logSentryEvent({
        message: SENTRY_MESSAGES.instrumentSubmitFailed,
        level: 'error',
        tags: {
          flow: 'instrument',
          action: 'submit_failed',
          instrument_id:
            resolvedInstrumentId != null
              ? String(resolvedInstrumentId)
              : undefined,
          instance_id:
            finalSurveyJSON?.instance_id != null
              ? String(finalSurveyJSON.instance_id)
              : undefined,
        },
      })
      dispatch(
        modalActions.openModal({
          heading: 'Network Error',
          content:
            'There was a problem submitting the survey, check your internet connection and click the button to try again.',
          className: 'web-survey-error',
          closeOnBackgroundClick: true,
          humi: !humiIsabellaDisabled,
          confirmButton: 'Retry',
          onConfirm: () =>
            dispatch(onSurveySubmit(survey, isArticle, articleJson)),
        }),
      )
    }
    dispatch(
      sendEvent(
        depQId,
        INSTRUMENT_EVENTS_MAP.completed,
        finalSurveyJSON?.instance_id,
      ),
    )

    if (!resolvedInstrumentType || !resolvedInstrumentId) {
      dispatch(
        modalActions.openModal({
          heading: 'Submission Error',
          content:
            'Unable to determine instrument metadata for submission. Please reload and try again.',
          className: 'web-survey-error',
          closeOnBackgroundClick: true,
          humi: !humiIsabellaDisabled,
          confirmButton: 'OK',
        }),
      )
      return Promise.resolve(false)
    }

    const stagedFilesForUpload = {
      ...collectImageFilesFromSurvey(survey),
      ...(getState().webSurveys?.stagedFiles || {}),
    }
    const photoFileNames = getPhotoFilenamesFromSurvey(survey)
    const missingPhotoFiles = photoFileNames.filter(
      (fileName) => !isBrowserFileUrl(stagedFilesForUpload[fileName]),
    )
    if (missingPhotoFiles.length) {
      logSentryEvent({
        message: 'File upload: upload_failed',
        level: 'error',
        tags: {
          flow: 'file-upload',
          'file.action': 'upload_missing_on_submit',
        },
        contexts: {
          fileUpload: {
            moduleName: 'web-survey',
            missingFiles: missingPhotoFiles.length,
          },
        },
      })
      dispatch(
        modalActions.openModal({
          heading: 'Photo Upload Error',
          content:
            'A photo could not be uploaded. Please go back, attach the photo again, and retry.',
          className: 'web-survey-error',
          closeOnBackgroundClick: true,
          humi: !humiIsabellaDisabled,
          confirmButton: 'OK',
        }),
      )
      return Promise.resolve(false)
    }

    const submissionUrl = isInstrumentArch1
      ? `/control/participants/me/instruments/${resolvedInstrumentId}/submission`
      : `/control/v4/data/${resolvedInstrumentType}`

    const submissionHeaders = isInstrumentArch1
      ? {
        'hpi-device-type': 'web',
        'hpi-version': '019',
        'hpi-utc-offset': getUTCOffset(),
      }
      : {
        'hpi-device-type': 'web',
        'hpi-version': '019',
        'hpi-utc-offset': getUTCOffset(),
        'hpi-hardware-id': 'web',
      }

    dispatch(loadingActions.startLoader(true, 'surveySubmission'))
    const filesToUpload = Object.fromEntries(
      photoFileNames.map((fileName) => [
        fileName,
        stagedFilesForUpload[fileName],
      ]),
    )
    return dispatch(uploadStagedBlobFiles(filesToUpload))
      .catch((error) => {
        dispatch(loadingActions.stopLoader(true, 'surveySubmission'))
        fail()
        throw error
      })
      .then(() =>
        dispatch(
          request({
            method: 'POST',
            url: submissionUrl,
            headerOptions: submissionHeaders,
            body: JSON.stringify([submissionPayload]),
            success,
            fail,
            hasLoader: true,
            loadingKey: 'surveySubmission',
          }),
        ),
      )
  }
}

const webSurvey = (state = {}, action) => {
  switch (action.type) {
    case SET_WEB_SURVEY:
      return prepareSurvey(action.instrument)
    case RESET_WEB_SURVEY:
      return {}
    default:
      return state
  }
}

const currentSurvey = (state = [], action) => {
  let newState = [...state]
  switch (action.type) {
    case ADD_QUESTION:
      newState = checkAndAddQuestion(
        newState,
        action.questionIds,
        action.order,
        action.justCompletedQuestion,
      )
      return newState
    case SET_ALL_REQUIRED_QUESTIONS_ANSWERED:
      if (action.allAnswered === false) {
        let cutoffQuestionIdx
        if (action.siblingOrder?.length) {
          const siblingIndexes = action.siblingOrder.map((siblingId) =>
            state.indexOf(siblingId),
          )
          cutoffQuestionIdx = Math.max(...siblingIndexes)
        } else {
          cutoffQuestionIdx = state.indexOf(action.id)
        }
        // Avoid clearing the survey when the question id is not in currentSurvey
        // (e.g. resize remounts sibling questions before their page id is registered).
        if (cutoffQuestionIdx < 0) {
          return state
        }
        newState.length = cutoffQuestionIdx + 1
      }
      if (action.allAnswered === true) {
        return action.order
      }
      return newState
    case RESET_CURRENT_SURVEY:
      return []
    default:
      return state
  }
}

const submissionSurvey = (state = {}, action) => {
  switch (action.type) {
    case SET_SUBMISSION_SURVEY: {
      const newState = Object.assign({}, action.survey)
      return newState
    }
    case ADD_COMPLETED_QUESTION:
      return {
        ...state,
        questions: checkAndAddCompletedQuestion(
          state.questions || [],
          action.question,
          action.order,
        ),
      }
    case SET_ALL_REQUIRED_QUESTIONS_ANSWERED: {
      if (!Array.isArray(state.questions)) {
        return state
      }

      const newState = { ...state, questions: [...state.questions] }

      const findQuestionIdx = (id) =>
        newState.questions.findIndex((question) => question.id === id)

      let cutoffQuestionIdx
      if (action.siblingOrder) {
        const siblingIndexes = action.siblingOrder.map((siblingId) =>
          findQuestionIdx(siblingId),
        )
        cutoffQuestionIdx = Math.max(...siblingIndexes)
      } else {
        cutoffQuestionIdx = findQuestionIdx(action.id)
      }
      // Avoid truncating when the question id is not in submissionSurvey yet
      // (e.g. resize remounts sibling questions before answers are persisted).
      if (cutoffQuestionIdx < 0) {
        return state
      }
      newState.questions.length = cutoffQuestionIdx + 1

      return newState
    }
    case RESET_SUBMISSION_SURVEY:
    default:
      return state
  }
}

const stagedFiles = (state = {}, action) => {
  switch (action.type) {
    case ADD_COMPLETED_QUESTION: {
      const { question = {} } = action
      const { imageFiles } = question
      const newState = { ...state }
      if (imageFiles) {
        Object.keys(imageFiles).forEach((fileName) => {
          newState[fileName] = imageFiles[fileName]
        })
      }
      return newState
    }
    case DELETE_STAGED_FILE: {
      const newState = { ...state }
      const { fileName } = action
      delete newState[fileName]
      return newState
    }
    case SET_SUBMISSION_SURVEY:
      return collectImageFilesFromSurvey(action.survey)
    case RESET_WEB_SURVEY:
    case RESET_CURRENT_SURVEY:
    case RESET_SUBMISSION_SURVEY:
      return {}
    default:
      return state
  }
}

export const webSurveyActions = {
  onFetchWebSurvey,
  onResetWebSurvey,
}

export const currentSurveyActions = {
  addQuestion,
  deleteStagedFile,
  onResetCurrentSurvey,
  setAllRequiredAnswered,
}

export const onSaveInstrumentToLocalStorage = () => {
  return (dispatch, getState) => {
    const { webSurveys, user } = getState()
    const { submissionSurvey, webSurvey } = webSurveys
    const participantId = user?.is_caregiver
      ? getCaregiverSelectedParticipantId() || user?.id
      : user?.id

    if (submissionSurvey && participantId) {
      const instrumentId =
        submissionSurvey.identity ||
        submissionSurvey.instrument_id ||
        submissionSurvey.id ||
        webSurvey?.identity ||
        webSurvey?.instrument_id ||
        webSurvey?.id
      if (instrumentId) {
        const scheduleId =
          submissionSurvey.schedule_id ||
          submissionSurvey.scheduleId ||
          webSurvey?.schedule_id ||
          webSurvey?.instances?.[0]?.schedule_id ||
          webSurvey?.instances?.[0]?.scheduleId
        const scheduleFromInstrument = getInstrumentSchedule(
          webSurvey,
          scheduleId,
        )
        const scheduleFromSubmission = getInstrumentSchedule(
          submissionSurvey,
          scheduleId,
        )
        const hasSchedule = (schedule) =>
          !!schedule &&
          typeof schedule === 'object' &&
          Object.keys(schedule).length > 0
        const resolvedSchedule = hasSchedule(scheduleFromInstrument)
          ? scheduleFromInstrument
          : hasSchedule(scheduleFromSubmission)
            ? scheduleFromSubmission
            : submissionSurvey.schedule ||
              submissionSurvey.metadata?.schedule ||
              webSurvey?.schedule ||
              webSurvey?.metadata?.schedule
        const stagedFiles = webSurveys?.stagedFiles || {}
        const questions = (submissionSurvey.questions || []).map((question) => {
          if (question?.type !== 'image_capture') return question
          const answerNames = (question.answer || [])
            .map((item) => item?.value)
            .filter(Boolean)
          const imageFiles = { ...(question.imageFiles || {}) }
          answerNames.forEach((fileName) => {
            if (stagedFiles[fileName]) {
              imageFiles[fileName] = stagedFiles[fileName]
            }
          })
          return { ...question, imageFiles }
        })

        saveInstrumentToLocalStorage({
          instrumentId,
          participantId,
          instrument: {
            ...submissionSurvey,
            questions,
            schedule_id: scheduleId || submissionSurvey.schedule_id,
            schedule: resolvedSchedule,
            metadata: {
              ...(submissionSurvey.metadata || {}),
              schedule: resolvedSchedule,
            },
          },
        })
      }
    }
  }
}

export const submissionSurveyActions = {
  completeQuestion,
  onResetSubmissionSurvey,
  onSetSubmissionSurvey,
  onSurveySubmit,
  onSaveInstrumentToLocalStorage,
}

export const uploadActions = {
  uploadV2File,
}

export default combineReducers({
  webSurvey,
  currentSurvey,
  stagedFiles,
  submissionSurvey,
})
